import { MdAccessTime, MdAttachFile, MdCheck, MdRepeat, MdSchedule } from "react-icons/md";
import Avatar from "components/avatar";
import { formatTime } from "utils/format";
import { getServiceColors, getConfirmationStatus } from "../utils/serviceColors";

/**
 * Calendar event card: service name, type, time, assigned people and
 * confirmation progress. Tinted by service type; the type is also written out.
 */
const EnhancedEventCard = ({ event }) => {
  const service = event?.extendedProps?.origin;
  const isRecurring = Boolean(event?.extendedProps?.isRecurring);
  const colors = getServiceColors(service?.type);
  const attachments = service?.attachments || [];
  const users = service?.users || [];
  const status = getConfirmationStatus(users);

  return (
    <div
      data-id={event?.id}
      className={`event-card group relative flex h-full w-full cursor-pointer flex-col gap-1.5 overflow-hidden rounded-control border p-3 transition-colors ${colors.border} ${colors.bg} ${colors.bgHover}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="line-clamp-2 flex-1 text-sm font-semibold leading-tight text-fg">
          {event?.title || "Untitled Service"}
        </h3>

        <div className="flex shrink-0 items-center gap-1">
          {isRecurring && (
            <span className={`flex items-center rounded-full p-1 ${colors.badge}`} title="Recurring">
              <MdRepeat aria-hidden="true" className="h-3 w-3" />
              <span className="sr-only">Recurring</span>
            </span>
          )}
          {attachments.length > 0 && (
            <span
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${colors.badge}`}
              title={`${attachments.length} attachment${attachments.length === 1 ? "" : "s"}`}
            >
              <MdAttachFile aria-hidden="true" className="h-3 w-3" />
              {attachments.length}
              <span className="sr-only">attachments</span>
            </span>
          )}
        </div>
      </div>

      <div className={`flex items-center gap-1.5 text-xs font-medium ${colors.text}`}>
        <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${colors.accent}`} />
        {service?.type || "Service"}
      </div>

      <div className="flex items-center gap-1 text-xs font-medium text-fg-muted">
        <MdAccessTime aria-hidden="true" className="h-3 w-3 shrink-0" />
        <span>
          {formatTime(event?.start)} - {formatTime(event?.end)}
        </span>
      </div>

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-line/60 pt-2">
        <div className="flex items-center -space-x-2">
          {users.length === 0 ? (
            <span className="text-xs italic text-fg-muted">No one assigned</span>
          ) : (
            <>
              {users.slice(0, 3).map((user, index) => (
                <div
                  key={`event-user-${user?.id}-${index}`}
                  className="rounded-full border-2 border-surface"
                  style={{ zIndex: 10 - index }}
                >
                  <Avatar user={user} size="xs" noBorder={true} />
                </div>
              ))}
              {users.length > 3 && (
                <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-surface bg-surface-subtle">
                  <span className="text-xs font-bold text-fg-muted">+{users.length - 3}</span>
                </div>
              )}
            </>
          )}
        </div>

        {users.length > 0 && status.confirmed > 0 && (
          <span className="flex items-center gap-1 text-xs font-medium text-success-fg">
            <MdCheck aria-hidden="true" className="h-3 w-3" />
            {status.confirmed}/{status.total}
            <span className="sr-only">confirmed</span>
          </span>
        )}
        {users.length > 0 && status.confirmed === 0 && status.pending > 0 && (
          <span className="flex items-center gap-1 text-xs font-medium text-warning-fg">
            <MdSchedule aria-hidden="true" className="h-3 w-3" />
            Pending
          </span>
        )}
      </div>
    </div>
  );
};

export default EnhancedEventCard;
